import { ExfluencyLoader } from 'editorNextGen/components/loader';
import i18next from 'i18next';
import { replaceUniversalTag } from './SpellcheckerModule';
import { infoOutlinedSvg } from 'editorNextGen/icons/infoOutlined';
export default class SpellcheckerPanel extends HTMLElement {
    constructor() {
        super();
        this.contentDiv = document.createElement("div");
        this.loader = document.createElement("div");
        this.emptyState = document.createElement("div");
        this.buttonsContainer = document.createElement("div");
        this.applyChangesButton = document.createElement("button");
        this._currentText = '';
        this._changes = [];
        this.className = "widget-box spellchecker-panel";
        const header = document.createElement("h1");
        header.className = "widget-box--header";
        header.textContent = i18next.t('editorSpellcheckerPanel.spellchecker');
        this.contentDiv.className = "spellchecker-content";
        this.emptyState.classList.add("emptyState");
        this.emptyState.textContent = i18next.t('editorSpellcheckerPanel.noCorrectionsNeeded');
        // Setup buttons
        this.setupButtons();
        this.loader.className = "loader";
        this.appendChild(header);
        this.appendChild(this.contentDiv);
        this.appendChild(this.buttonsContainer);
        this.appendChild(this.emptyState);
        this.appendChild(this.loader);
        this.updateDisplay();
    }
    getSimpleTooltip() {
        const simpleTooltip = document.querySelector("xfl-new-popup-ele");
        simpleTooltip.content = i18next.t('editorSpellcheckerPanel.hoverTooltip');
        return simpleTooltip;
    }
    setupButtons() {
        this.buttonsContainer.className = "spellchecker-buttons";
        this.applyChangesButton.className = "btn btn-primary";
        this.applyChangesButton.textContent = "Accept & complete";
        this.applyChangesButton.addEventListener('click', () => {
            if (this.onApplyAndComplete) {
                this.onApplyAndComplete(this._changes);
            }
        });
        // Create info icon with tooltip
        const infoIcon = document.createElement('span');
        infoIcon.className = 'spellchecker-info-icon';
        infoIcon.innerHTML = infoOutlinedSvg;
        infoIcon.addEventListener('mouseenter', (e) => {
            this.getSimpleTooltip().showTooltip(infoIcon, e, 'top');
        });
        infoIcon.addEventListener('mouseleave', () => {
            this.getSimpleTooltip().hide();
        });
        this.buttonsContainer.appendChild(this.applyChangesButton);
        this.buttonsContainer.appendChild(infoIcon);
    }
    set currentText(text) {
        this._currentText = text;
        this.updateDisplay();
    }
    set changes(changes) {
        this._changes = changes || [];
        this.updateDisplay();
    }
    showLoader() {
        this.contentDiv.style.display = 'none';
        this.loader.style.display = 'flex';
        this.loader.appendChild(new ExfluencyLoader(false, 'transparent'));
        this.buttonsContainer.style.display = 'none';
        this.emptyState.style.display = 'none';
    }
    checkAndCompleteIfAllResolved() {
        // Check if all changes have been resolved (accepted is either true or false, not undefined)
        const allResolved = this._changes.every(change => change.accepted !== undefined);
        if (allResolved && this.onApplyAndComplete) {
            // Small delay to allow the UI to update before completing
            setTimeout(() => {
                if (this.onApplyAndComplete) {
                    this.onApplyAndComplete(this._changes);
                }
            }, 300);
        }
    }
    createCorrectionPopup(change, correctionContainer) {
        const popup = document.createElement('div');
        popup.className = 'correction-popup';
        // Label
        const label = document.createElement('div');
        label.className = 'correction-popup-label';
        label.textContent = 'Confirm version:';
        // Options container
        const options = document.createElement('div');
        options.className = 'correction-popup-options';
        // Original text option (clickable)
        const originalOption = document.createElement('div');
        originalOption.className = 'correction-popup-option correction-popup-original';
        const previousFragment = readableOriginalFragment(change.previous_fragment);
        originalOption.innerHTML = previousFragment;
        originalOption.addEventListener('click', () => {
            change.accepted = false;
            // Remove the popup from DOM
            popup.remove();
            // Replace correction container content with the original text
            correctionContainer.innerHTML = '';
            const originalText = document.createElement('span');
            originalText.innerHTML = change.previous_fragment;
            correctionContainer.appendChild(originalText);
            // Check if all changes are resolved
            this.checkAndCompleteIfAllResolved();
        });
        // Corrected text option (clickable)
        const correctedOption = document.createElement('div');
        correctedOption.className = 'correction-popup-option correction-popup-corrected';
        correctedOption.innerHTML = readableCorrectedFragment(change.new_fragment);
        correctedOption.addEventListener('click', () => {
            change.accepted = true;
            // Remove the popup from DOM
            popup.remove();
            // Replace correction container content with the accepted text
            correctionContainer.innerHTML = '';
            const acceptedText = document.createElement('span');
            acceptedText.innerHTML = change.new_fragment;
            correctionContainer.appendChild(acceptedText);
            // Check if all changes are resolved
            this.checkAndCompleteIfAllResolved();
        });
        options.appendChild(originalOption);
        options.appendChild(correctedOption);
        popup.appendChild(label);
        popup.appendChild(options);
        // Add popup to document body
        document.body.appendChild(popup);
        // Popup positioning and event handling logic
        let hideTimeout = null;
        const showPopup = () => {
            const rect = correctionContainer.getBoundingClientRect();
            popup.style.display = 'block';
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.top + window.scrollY - popup.offsetHeight - 4}px`;
        };
        const hidePopup = () => {
            hideTimeout = window.setTimeout(() => {
                popup.style.display = 'none';
            }, 100); // short delay to allow moving between elements
        };
        const cancelHide = () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
                hideTimeout = null;
            }
        };
        correctionContainer.addEventListener('mouseenter', () => {
            cancelHide();
            showPopup();
        });
        correctionContainer.addEventListener('mouseleave', hidePopup);
        popup.addEventListener('mouseenter', cancelHide);
        popup.addEventListener('mouseleave', hidePopup);
        return popup;
    }
    updateDisplay() {
        this.loader.style.display = 'none';
        this.loader.innerHTML = '';
        this.contentDiv.innerHTML = "";
        if (!this._currentText || this._changes.length === 0) {
            this.style.display = 'none';
            return;
        }
        this.style.display = 'block';
        this.emptyState.style.display = 'none';
        this.contentDiv.style.display = 'block';
        this.buttonsContainer.style.display = 'flex';
        const resultDiv = document.createElement('div');
        resultDiv.className = 'spellcheck-result';
        // Sort changes by start position to process them in order
        const sortedChanges = [...this._changes].sort((a, b) => a.start_pos - b.start_pos);
        let currentIndex = 0;
        const originalText = this._currentText;
        sortedChanges.forEach((change) => {
            // Add text before this change
            if (change.start_pos > currentIndex) {
                const beforeText = originalText.substring(currentIndex, change.start_pos);
                if (beforeText) {
                    resultDiv.insertAdjacentHTML('beforeend', replaceUniversalTag(beforeText));
                }
            }
            // Create correction element
            const correctionContainer = document.createElement('span');
            correctionContainer.className = 'correction-container';
            if (change.start_pos === change.end_pos) {
                // Insertion case (missing text) - show new text with same styling as corrections
                const insertedText = document.createElement('span');
                insertedText.className = 'corrected-text';
                insertedText.innerHTML = replaceUniversalTag(change.new_fragment);
                correctionContainer.appendChild(insertedText);
                // For insertions, we don't advance the currentIndex since no text was replaced
                currentIndex = change.end_pos;
            }
            else {
                const correctedText = document.createElement('span');
                correctedText.className = 'corrected-text';
                correctedText.innerHTML = replaceUniversalTag(change.new_fragment);
                correctionContainer.appendChild(correctedText);
                // Move past the replaced text
                currentIndex = change.end_pos;
            }
            // --- POPUP LOGIC ---
            const popup = this.createCorrectionPopup(change, correctionContainer);
            // --- END POPUP LOGIC ---
            resultDiv.appendChild(correctionContainer);
        });
        // Add remaining text after last change
        if (currentIndex < originalText.length) {
            const remainingText = originalText.substring(currentIndex);
            if (remainingText) {
                resultDiv.insertAdjacentHTML('beforeend', replaceUniversalTag(remainingText));
            }
        }
        this.contentDiv.appendChild(resultDiv);
    }
}
document.addEventListener("DOMContentLoaded", () => customElements.define("xfl-new-editor-spellchecker-panel", SpellcheckerPanel));
const readableOriginalFragment = (fragment) => {
    return (fragment.trim() === '' && fragment.length > 0) ? '[redundant whitespace]' : fragment;
};
const readableCorrectedFragment = (fragment) => {
    return (fragment === '') ? '[empty text]' : fragment;
};
